import { useEffect, useState } from "react";

import { IStaffUser } from "../../../../@types/admin/AdminStaff";
import { useUpdateStaffUser } from "../../../../hooks/mutations/admin/staff";
import { useLanguages } from "../../../../hooks/queries/languages";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FieldError,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  ReadOnlyValue,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";

interface IStaffUserFormProps {
  user: IStaffUser;
}

interface IUserFormState {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isTestAccount: boolean;
  languageCode: string;
}

function toFormState(user: IStaffUser): IUserFormState {
  return {
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    phoneNumber: user.phoneNumber ?? "",
    isTestAccount: user.isTestAccount,
    languageCode: user.languageCode ?? "",
  };
}

/**
 * The linked user account half of the details panel — Retool's
 * `formUserDetails` (EN-5150). Email is read-only here exactly as it was in
 * Retool: changing it means moving the Cognito identity, which is not what
 * this form does.
 */
export function StaffUserForm({ user }: IStaffUserFormProps) {
  const { showToast } = useToast();
  const { updateStaffUser, isUpdatingStaffUser } = useUpdateStaffUser();
  // The languages the portal has translations for: a staff member put on any
  // other one would just see English (EN-5292).
  const { languages, isLanguagesFetching } = useLanguages();

  const [form, setForm] = useState<IUserFormState>(() => toFormState(user));

  useEffect(() => {
    setForm(toFormState(user));
  }, [user]);

  const errors = {
    firstName: form.firstName.trim() ? undefined : "First name is required.",
    lastName: form.lastName.trim() ? undefined : "Last name is required.",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  // A stored code the list does not carry is still shown, rather than the
  // field pretending the user has no language.
  const languageOptions =
    user.languageCode &&
    !languages.some((language) => language.code === user.languageCode)
      ? [...languages, { code: user.languageCode }]
      : languages;

  const handleSave = async () => {
    if (hasErrors) return;

    try {
      await updateStaffUser({
        id: user.id,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim() || undefined,
        // Sent unchanged: the API treats the field as part of the update, and
        // the input is read-only.
        email: user.email,
        isTestAccount: form.isTestAccount,
        // Only when changed, so a save never rewrites a language it did not touch.
        languageCode:
          form.languageCode && form.languageCode !== user.languageCode
            ? form.languageCode
            : undefined,
      });
      showToast("The user details were updated successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating the user details."
        ),
        "warning"
      );
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>User details</SectionTitle>
          <SectionCaption>ID {user.id}</SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel>Email</FilterLabel>
          <ReadOnlyValue>{user.email || "-"}</ReadOnlyValue>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="user-first-name">First name</FilterLabel>
          <FilterInput
            id="user-first-name"
            type="text"
            value={form.firstName}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, firstName: e.target.value }))
            }
          />
          {errors.firstName && <FieldError>{errors.firstName}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="user-last-name">Last name</FilterLabel>
          <FilterInput
            id="user-last-name"
            type="text"
            value={form.lastName}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, lastName: e.target.value }))
            }
          />
          {errors.lastName && <FieldError>{errors.lastName}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="user-phone">Phone number</FilterLabel>
          <FilterInput
            id="user-phone"
            type="tel"
            placeholder="+353..."
            value={form.phoneNumber}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, phoneNumber: e.target.value }))
            }
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="user-language">Language</FilterLabel>
          <AdminSelectField
            id="user-language"
            value={form.languageCode}
            disabled={isLanguagesFetching}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, languageCode: e.target.value }))
            }
          >
            <option value="">
              {isLanguagesFetching ? "Loading..." : "Select a language"}
            </option>
            {languageOptions.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name || language.code}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Account</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.isTestAccount}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  isTestAccount: e.target.checked,
                }))
              }
            />
            Test account
          </CheckboxField>
        </FilterGroup>
      </FormGrid>

      <SectionActions>
        <FilterButton
          variant="primary"
          disabled={isUpdatingStaffUser || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingStaffUser ? "Saving..." : "Save"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
